"use client";

import { useState, useEffect } from "react";
import { Menu, Facebook, Phone, Mail, Instagram, Twitter, Youtube, Smartphone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i - 1} className="h-4 w-16 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex font-sans">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-bold uppercase tracking-widest text-primary transition-all duration-300 hover:text-secondary group py-2">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-secondary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems
  } = useMenu();
  return <nav className="flex flex-col gap-4 mt-8 font-sans">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-semibold text-primary hover:text-secondary border-b border-muted pb-2 transition-colors">
          {item.label}
        </Link>)}
      <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Phone className="size-4 text-secondary" /> 323-492-6292
        </div>
        <div className="flex items-center gap-2">
          <Mail className="size-4 text-secondary" /> phoenixenerprises@gmail.com
        </div>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleFormSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const formIdentifier = (form.id || form.name || form.className || "").toLowerCase();
      if (formIdentifier.includes("booking") || formIdentifier.includes("reservation") || formIdentifier.includes("quote")) {
        if (window.gtag) {
          window.gtag('event', 'generate_lead');
        }
      }
    };
    document.addEventListener("submit", handleFormSubmit, {
      capture: true
    });
    return () => document.removeEventListener("submit", handleFormSubmit, {
      capture: true
    });
  }, []);
  useEffect(() => {
    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = "https://www.googletagmanager.com/gtag/js?id=G-VPC1CFW7YY";
    document.head.appendChild(script1);
    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-VPC1CFW7YY');
      gtag('event', 'conversion', {'send_to': 'AW-11528447310/f_Y_CJ_Y_p0ZEM7_9-ko'});
    `;
    document.head.appendChild(script2);
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-background/80 backdrop-blur-lg shadow-medium' : 'bg-background'}`}>
      <div className="bg-primary text-white py-2 hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="tel:3234926292" className="hover:text-secondary transition-colors flex items-center gap-2 text-xs font-sans">
              <Phone className="size-3" /> 323-492-6292
            </Link>
            <Link to="sms:3234926292" className="hover:text-secondary transition-colors flex items-center gap-2 text-xs font-sans">
              <Smartphone className="size-3" /> Text Us
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="#" className="hover:text-secondary transition-colors"><Facebook className="size-4" /></Link>
            <Link to="#" className="hover:text-secondary transition-colors"><Twitter className="size-4" /></Link>
            <Link to="#" className="hover:text-secondary transition-colors"><Youtube className="size-4" /></Link>
            <Link to="#" className="hover:text-secondary transition-colors"><Instagram className="size-4" /></Link>
          </div>
        </div>
      </div>

      <div className={`border-b border-muted transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0">
            <WvcLogo className="h-12 md:h-16 w-auto" />
          </Link>

          <div className="hidden md:flex flex-col items-end justify-end gap-1 font-sans">
            <Link to="mailto:phoenixenerprises@gmail.com" className="text-primary font-bold hover:text-secondary transition-colors text-sm">
              phoenixenerprises@gmail.com
            </Link>
            <div className="flex gap-6 text-lg font-black text-foreground">
              <div className="flex items-center gap-2">
                <span className="text-xs font-normal text-muted-foreground uppercase tracking-tighter">Call:</span>
                <Link to="tel:3234926292" className="hover:text-secondary transition-colors">323-492-6292</Link>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-normal text-muted-foreground uppercase tracking-tighter">Text:</span>
                <Link to="sms:3234926292" className="hover:text-secondary transition-colors">323-492-6292</Link>
              </div>
            </div>
          </div>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-primary hover:text-secondary transition-colors">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <Link to="/">
                      <WvcLogo className="h-10 w-auto" />
                    </Link>
                  </SheetTitle>
                </SheetHeader>
                <MenuProvider menu_id="45">
                  <MobileMenuConsumer />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className={`border-b border-muted hidden md:block transition-all duration-300 ${isScrolled ? 'bg-transparent' : 'bg-white'}`}>
        <div className="container mx-auto px-4 flex items-center justify-center py-3">
          <MenuProvider menu_id="45">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}